import React, { memo, useEffect, useState } from 'react';

import { DSelect, HCheckbox, HInput, passParamsThrough } from '../../../validation/HForm';
import { components } from 'react-select';
import { searchService } from '../../../services/search-service';

const AddressShortAdd = memo(
  ({
    unregister,
    control,
    register,
    errors,
    reset,
    getValues,
    setValue,
    tempEntity,
    hPrefixNames,
  }) => {
    const props = {
      control,
      register,
      errors,
    };
    const prefix = hPrefixNames || '';

    useEffect(() => {
      reset({
        [`${prefix}region`]: tempEntity?.region,
        [`${prefix}cityType`]: tempEntity?.cityType,
        [`${prefix}city`]: tempEntity?.city,
        [`${prefix}streetType`]: tempEntity?.streetType,
        [`${prefix}street`]: tempEntity?.street,
        [`${prefix}house`]: tempEntity?.house,
        [`${prefix}building`]: tempEntity?.building,
        [`${prefix}apartment`]: tempEntity?.apartment,
        [`${prefix}soato`]: tempEntity?.soato,
        [`${prefix}active`]: tempEntity?.active,
        [`${prefix}postalCode`]: tempEntity?.postalCode,
      });
      return () => {
        if (unregister) {
          unregister(`${prefix}active`);
        }
      };
    }, []);

    const [regionLoading, setRegionLoading] = useState(false);
    const [regions, setRegions] = useState([]);
    const [regionSoato, setRegionSoato] = useState(tempEntity?.soato ? tempEntity?.soato[0] : '');

    const [cityLoading, setCityLoading] = useState(false);
    const [cities, setCities] = useState([]);
    const [citySoato, setCitySoato] = useState(tempEntity?.soato || '');

    const [streetLoading, setStreetLoading] = useState(false);
    const [streets, setStreets] = useState([]);

    useEffect(() => {
      getRegions();
    }, []);

    useEffect(() => {
      getCities();
    }, [regionSoato]);

    useEffect(() => {
      getStreets();
    }, [regionSoato, citySoato]);

    const getRegions = (syl) => {
      setRegionLoading(true);
      searchService
        .getAddresses({ level: 'region', term: syl })
        .then(({ data: { buckets } }) => {
          if (buckets) {
            let bckts = buckets.map((bucket) => {
              return {
                label: bucket.name,
                value: bucket.name,
                soato: bucket.soato,
              };
            });

            setRegions(bckts);
          }
        })
        .catch(() => {})
        .finally(() => {
          setRegionLoading(false);
        });
    };

    const getCities = (syl) => {
      setCityLoading(true);
      searchService
        .getAddresses({ level: 'city', term: syl, soato: regionSoato })
        .then(({ data: { buckets } }) => {
          if (buckets) {
            let bckts = buckets.map((bucket) => {
              return {
                label: bucket.type + ' ' + bucket.name,
                value: bucket.type + ' ' + bucket.name,
                name: bucket.name,
                soato: bucket.soato,
                region: bucket.region,
                subLabel: `${bucket.region} обл`,
                type: bucket.type,
              };
            });

            setCities(bckts);
          }
        })
        .catch(() => {})
        .finally(() => {
          setCityLoading(false);
        });
    };

    const getStreets = (syl) => {
      setStreetLoading(true);
      searchService
        .getAddresses({
          level: 'street',
          term: syl,
          soato: citySoato || regionSoato,
        })
        .then(({ data: { addresses } }) => {
          if (addresses) {
            let addrs = addresses.map((address) => {
              return {
                label: address.streetType + ' ' + address.street,
                value: address.streetType + ' ' + address.street,
                soato: address.soato,
                type: address.streetType,
                name: address.street,
                city: address.city,
                subLabel: `${address.region} обл, ${address.cityType} ${address.city}`,
                cityType: address.cityType,
                region: address.region,
              };
            });
            if (addrs.length === 0 && syl) {
              let values = getValues();
              addrs = [
                {
                  label: syl,
                  value: syl,
                  soato: values.soato,
                  type: '-',
                  name: syl,
                  city: values.city?.name,
                  subLabel: '',
                  cityType: values.cityType,
                  region: values.region?.value,
                },
              ];
            }

            setStreets(addrs);
          }
        })
        .catch(() => {})
        .finally(() => {
          setStreetLoading(false);
        });
    };

    const handleRegions = (syl, action) => {
      if (action.action === 'input-change') getRegions(syl);
    };
    const handleCities = (syl, action) => {
      if (action.action === 'input-change') getCities(syl);
    };
    const handleStreets = (syl, action) => {
      if (action.action === 'input-change') getStreets(syl);
    };

    const clearStreet = () => {
      setValue(`${prefix}street`, { label: '', value: '', name: '' }, { shouldValidate: false });
      setValue(`${prefix}streetType`, '', { shouldValidate: false });
    };

    const clearCity = () => {
      setValue(`${prefix}city`, { label: '', value: '', name: '' }, { shouldValidate: false });
      setValue(`${prefix}cityType`, '', { shouldValidate: false });
    };

    const handleCitiesChange = (e) => {
      if (e) {
        setValue(
          `${prefix}city`,
          { label: e.label, value: e.value, name: e.name },
          { shouldValidate: true },
        );
        setValue(`${prefix}cityType`, e.type, { shouldValidate: true });
        setValue(`${prefix}region`, { label: e.region, value: e.region }, { shouldValidate: true });

        let soato = e.soato;
        if (e.soato && e.soato.length === 7) {
          soato = e.soato + '000';
        }
        setValue(`${prefix}soato`, soato, { shouldValidate: false });

        clearStreet();
        setCitySoato(e.soato);
        setRegionSoato(e.soato && e.soato[0]);
      } else {
        setValue(`${prefix}city`, { label: '', value: '' }, { shouldValidate: true });
        setValue(`${prefix}cityType`, '', { shouldValidate: true });
      }
    };

    const handleStreetsChange = (e) => {
      setValue(
        `${prefix}street`,
        { label: e.label, value: e.value, name: e.name },
        { shouldValidate: true },
      );
      setValue(`${prefix}streetType`, e.type, { shouldValidate: true });
      setValue(
        `${prefix}city`,
        {
          label: e.city ? e.cityType + ' ' + e.city : '',
          value: e.city ? e.cityType + ' ' + e.city : '',
          name: e.city,
        },
        { shouldValidate: true },
      );
      setValue(`${prefix}cityType`, e.cityType, { shouldValidate: true });
      setValue(`${prefix}region`, { label: e.region, value: e.region }, { shouldValidate: true });
      if (e.soato) {
        setValue(`${prefix}soato`, e.soato, { shouldValidate: false });
      }
      setCitySoato(e.soato);
      setRegionSoato(e.soato && e.soato[0]);
    };

    const handleRegionsChange = (e) => {
      if (e) {
        setValue(`${prefix}region`, { label: e.label, value: e.value }, { shouldValidate: true });
        clearStreet();
        clearCity();
        setCitySoato('');
        setRegionSoato(e.soato);
      } else {
        setValue(`${prefix}region`, { label: '', value: '' }, { shouldValidate: true });
      }
    };

    const Option = (props) => {
      return (
        <components.Option {...props}>
          <div>{props.data.label}</div>
          <div style={{ fontSize: 12 }}>{props.data.subLabel}</div>
        </components.Option>
      );
    };

    return passParamsThrough(
      <fieldset>
        <HInput
          hidden={true}
          h="true"
          className="input"
          type="text"
          name={`${prefix}soato`}
          id="soato"
        />
        <div className="form-row">
          <div className="form-row-item">
            <div className="form-element">
              <label className="form-element-label" htmlFor="country">
                Страна
              </label>
              <div className="form-element-data combobox">
                <DSelect
                  h="true"
                  name={`${prefix}country`}
                  defaultValue={{ value: 0, label: 'Беларусь' }}
                  placeholder="Введите страну"
                  isDisabled={true}
                />
              </div>
            </div>
          </div>
          <div className="form-row-item">
            <div className="form-element">
              <label className="form-element-label" htmlFor="region">
                Область
              </label>
              <div className="form-element-data combobox">
                <DSelect
                  classNamePrefix="option-empty-select"
                  h="true"
                  name={`${prefix}region`}
                  isLoading={regionLoading}
                  isClearable={true}
                  options={regions}
                  handleOptions={handleRegions}
                  onChange={handleRegionsChange}
                  placeholder="Введите область"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <div className="form-element">
              <label className="form-element-label" htmlFor="city">
                Населенный пункт
              </label>
              <div className="form-element-data combobox-lg ">
                <HInput
                  hidden={true}
                  h="true"
                  className="input"
                  type="text"
                  name={`${prefix}cityType`}
                  id="cityType"
                />
                <DSelect
                  h="true"
                  classNamePrefix="option-empty-select"
                  name={`${prefix}city`}
                  isLoading={cityLoading}
                  options={cities}
                  isClearable={true}
                  cmpnts={{ Option }}
                  handleOptions={handleCities}
                  onChange={handleCitiesChange}
                  placeholder="Введите населенный пункт"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <div className="form-element">
              <label className="form-element-label" htmlFor="contactsStreet">
                Улица
              </label>
              <div className="form-element-data combobox-lg ">
                <HInput
                  hidden={true}
                  h="true"
                  className="input"
                  type="text"
                  name={`${prefix}streetType`}
                  id="streetType"
                />
                <DSelect
                  h="true"
                  classNamePrefix="option-empty-select"
                  name={`${prefix}street`}
                  isLoading={streetLoading}
                  options={streets}
                  cmpnts={{ Option }}
                  handleOptions={handleStreets}
                  onChange={handleStreetsChange}
                  placeholder="Введите улицу"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <div className="form-element">
              <label className="form-element-label" htmlFor="house">
                Дом
              </label>
              <div className="form-element-data number">
                <HInput h="true" className="input" type="text" name={`${prefix}house`} id="house" />
              </div>
            </div>
          </div>
          <div className="form-row-item">
            <div className="form-element">
              <label className="form-element-label" htmlFor="building">
                Корпус
              </label>
              <div className="form-element-data number">
                <HInput
                  h="true"
                  className="input"
                  type="text"
                  name={`${prefix}building`}
                  id="building"
                />
              </div>
            </div>
          </div>
          <div className="form-row-item">
            <div className="form-element">
              <label className="form-element-label" htmlFor="apartment">
                Квартира
              </label>
              <div className="form-element-data number">
                <HInput
                  h="true"
                  className="input"
                  type="text"
                  name={`${prefix}apartment`}
                  id="apartment"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-row-item">
            <div className="form-element">
              <label className="form-element-label" htmlFor="clientAddCountry">
                Активен
              </label>
              <div className="form-element-data">
                <HCheckbox h="true" label="Активен" name="active" />
              </div>
            </div>
          </div>
          <div className="form-row-item">
              <div className="form-element">
                <label
                  className="form-element-label"
                  htmlFor="clientHelperDescription">
                  Индекс
                </label>
                <div className="form-element-data">
                  <HInput
                    h="true"
                    className="input"
                    type="text"
                    name={`${prefix}postalCode`}
                    id="postalCode"/>
                </div>
              </div>
            </div>
        </div>
       
      </fieldset>,
      props,
    );
  },
);

export default AddressShortAdd;
